import {
  AlertTriangle,
  Eye,
  FileOutput,
  FileText,
  Loader2,
  PencilLine,
  ScanSearch,
  ShieldAlert,
  type LucideIcon
} from "lucide-react";
import type { FC } from "react";

import { Badge } from "@/components/ui/badge";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import {
  CATEGORY_DESCRIPTION,
  CATEGORY_LABEL,
  categoryOf,
  CONVERSION_BADGE_LABEL,
  CONVERSION_BADGE_TITLE,
  conversionBadgeOf,
  formatLabel,
  hasOpenClientDetails,
  needsReview,
  READINESS_LABEL,
  readinessOf,
  type ConversionBadgeKind
} from "../model";
import type { FirmLibraryEntry } from "../types";

export const FormatBadge: FC<{
  entry: Pick<
    FirmLibraryEntry,
    "mime_type" | "original_filename" | "is_pdf" | "authorable"
  >;
  className?: string;
}> = ({ entry, className }) => (
  <Badge
    variant="outline"
    className={cn("font-mono text-xs tracking-wide", className)}
  >
    {formatLabel(entry)}
  </Badge>
);

export const ReadinessBadge: FC<{
  entry: FirmLibraryEntry;
  className?: string;
}> = ({ entry, className }) => {
  const readiness = readinessOf(entry);
  const label = READINESS_LABEL[readiness];
  const tone = (
    {
      ready: "success",
      preparing: "info",
      attention: "warn",
      describe: "purple"
    } as const
  )[readiness];
  const Icon = {
    ready: null,
    preparing: Loader2,
    attention: AlertTriangle,
    describe: PencilLine
  }[readiness];
  return (
    <StatusBadge
      tone={tone}
      dot={!Icon}
      className={className}
      data-testid="entry-readiness"
      data-readiness={readiness}
      title={
        readiness === "attention" && entry.rendering_error
          ? entry.rendering_error
          : readiness === "describe"
          ? "The agent could not read this file. Open it and write a description; it goes live when you save."
          : undefined
      }
    >
      {Icon ? (
        <Icon className={cn(readiness === "preparing" && "animate-spin")} />
      ) : null}
      {label}
    </StatusBadge>
  );
};

const CONVERSION_TONE: Record<ConversionBadgeKind, StatusTone> = {
  pending: "info",
  check: "warn",
  review: "warn",
  kept_pdf: "neutral",
  unused: "warn"
};

const CONVERSION_ICON: Record<ConversionBadgeKind, LucideIcon> = {
  pending: Loader2,
  check: AlertTriangle,
  review: ScanSearch,
  kept_pdf: FileText,
  unused: FileOutput
};

const CONVERSION_MARK: Record<StatusTone, string> = {
  neutral: "text-fg-3",
  accent: "text-accent-seed",
  success: "text-success",
  warn: "text-warn",
  danger: "text-danger",
  info: "text-info",
  purple: "text-purple",
  orange: "text-orange"
};

/**
 * A PDF's conversion when it is worth a word: being converted, converted but
 * worth a page check, kept as a PDF by choice, or a copy not applied. Nothing
 * for a native docx, a cleanly converted entry (its icon says so) or a
 * refusal (the drawer explains it). `compact` is the list's marker: the icon
 * alone, with the label as its tooltip.
 */
export const ConversionBadge: FC<{
  entry: Pick<FirmLibraryEntry, "conversion">;
  compact?: boolean;
  className?: string;
}> = ({ entry, compact = false, className }) => {
  const kind = conversionBadgeOf(entry);
  if (!kind) return null;
  const tone = CONVERSION_TONE[kind];
  const Icon = CONVERSION_ICON[kind];
  const spin = kind === "pending" && "animate-spin";
  if (compact) {
    return (
      <span
        role="img"
        aria-label={CONVERSION_BADGE_LABEL[kind]}
        className={cn("inline-flex", CONVERSION_MARK[tone], className)}
        data-testid="entry-conversion"
        data-conversion={kind}
        title={`${CONVERSION_BADGE_LABEL[kind]}. ${CONVERSION_BADGE_TITLE[kind]}`}
      >
        <Icon className={cn("size-3.5", spin)} />
      </span>
    );
  }
  return (
    <StatusBadge
      tone={tone}
      className={className}
      data-testid="entry-conversion"
      data-conversion={kind}
      title={CONVERSION_BADGE_TITLE[kind]}
    >
      <Icon className={cn(spin)} />
      {CONVERSION_BADGE_LABEL[kind]}
    </StatusBadge>
  );
};

export const CategoryBadge: FC<{
  entry: Pick<FirmLibraryEntry, "category" | "effective_category" | "tags">;
  className?: string;
}> = ({ entry, className }) => {
  const category = categoryOf(entry);
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 text-xs font-normal text-muted-foreground",
        className
      )}
      title={CATEGORY_DESCRIPTION[category]}
      data-testid="entry-category"
      data-category={category}
    >
      {CATEGORY_LABEL[category]}
    </Badge>
  );
};

/** Another client's identifiers are in the document and nobody has dealt with them. */
export const ClientDetailsBadge: FC<{
  entry: Pick<FirmLibraryEntry, "client_details">;
  className?: string;
}> = ({ entry, className }) => {
  if (!hasOpenClientDetails(entry)) return null;
  const kinds = entry.client_details?.kinds ?? [];
  return (
    <StatusBadge
      tone="danger"
      className={className}
      title={`Contains ${
        kinds.join(", ") || "client details"
      } from another matter. The agents never copy them into a case.`}
      data-testid="entry-client-details"
    >
      <ShieldAlert />
      Client details
    </StatusBadge>
  );
};

export const NotReviewedBadge: FC<{
  entry: Pick<FirmLibraryEntry, "auto_activated" | "reviewed_at" | "status">;
  className?: string;
}> = ({ entry, className }) => {
  if (!needsReview(entry)) return null;
  return (
    <StatusBadge
      tone="purple"
      className={className}
      title="The agent described this document and it went live. Nobody at the firm has checked it yet."
      data-testid="entry-not-reviewed"
    >
      <Eye />
      Not reviewed yet
    </StatusBadge>
  );
};

/**
 * The drawer header's one status: the most urgent of client details, a
 * readiness problem, a missing review and the PDF's conversion; Ready when
 * none applies.
 */
export const EntryStatusBadge: FC<{ entry: FirmLibraryEntry }> = ({
  entry
}) => {
  if (hasOpenClientDetails(entry)) return <ClientDetailsBadge entry={entry} />;
  if (readinessOf(entry) !== "ready") return <ReadinessBadge entry={entry} />;
  if (needsReview(entry)) return <NotReviewedBadge entry={entry} />;
  if (conversionBadgeOf(entry)) return <ConversionBadge entry={entry} />;
  return <ReadinessBadge entry={entry} />;
};

/** Up to two muted "#tag"s and a "+N" under a row's title. */
export const HashTags: FC<{
  tags: string[];
  max?: number;
  className?: string;
}> = ({ tags, max = 2, className }) => {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  const rest = tags.length - shown.length;
  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-1 overflow-hidden text-sm whitespace-nowrap",
        className
      )}
      data-testid="entry-tags"
    >
      {shown.map((tag) => (
        <span key={tag} className="truncate text-fg-3">
          #{tag}
        </span>
      ))}
      {rest > 0 ? <span className="shrink-0 text-fg-4">+{rest}</span> : null}
    </span>
  );
};
